import { FC, useState } from 'react';
import { Avatar, Button, Col, Dropdown, Menu, Row, Typography } from 'antd';
import { TbArrowRight, TbDatabaseCog, TbPlugConnected, TbSettings, TbX } from 'react-icons/tb';

import { GoogleSheetsSVGIcon, NotionSVGIcon } from '@/assets';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { BaseSkeleton, DashedPane, DropdownMenuPanel, EmptySection, VirtualizedList as List, SmallTag } from '@/shared';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import AddDataSourceModal from '@/shared/components/modals/ResourceCreationModals/AddDataSourceModal';
import { imgLogoStyling } from '@/theme';
import { ConnectedAccount, ConnectedAccountType, SetStateMethod } from '@/types';
import { formatDate } from '@/utilities';

interface DataSourcesProps {
    connectedAccounts: ConnectedAccount[];
    loadingConnectedAccounts?: boolean;
    selectedConnectedAccounts: any;
    setSelectedConnectedAccounts: any;
    setSelectedNotionDatabaseId: SetStateMethod<any>;
}

const DataSources: FC<DataSourcesProps> = ({
    connectedAccounts,
    loadingConnectedAccounts = false,
    selectedConnectedAccounts,
    setSelectedConnectedAccounts,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const [addDataSourceModalOpen, setAddDataSourceModalOpen] = useState(false);

    const syncableDataSourceTypes = new Set<ConnectedAccountType>([
        ConnectedAccountType.GOOGLE_SHEETS,
        ConnectedAccountType.NOTION,
    ]);

    const syncableConnectedAccounts = connectedAccounts.filter((connectedAccount) => {
        return connectedAccount.type ? syncableDataSourceTypes.has(connectedAccount.type) : false;
    });

    const typeToIconMap = {
        [ConnectedAccountType.GOOGLE_SHEETS]: GoogleSheetsSVGIcon,
        [ConnectedAccountType.NOTION]: NotionSVGIcon,
    } as Record<string, any>;

    const renderManageDataSourcesButton = () => (
        <Button
            size='small'
            type='dashed'
            onClick={() => {
                setAddDataSourceModalOpen(true);
            }}
        >
            <small style={{ color: token.colorTextTertiary, verticalAlign: '0.05rem' }}>
                <TbSettings style={{ verticalAlign: '-0.1rem', marginRight: '0.25rem' }} />
                Manage Data Sources
                <TbArrowRight style={{ verticalAlign: '-0.1rem', marginLeft: '0.25rem' }} />
            </small>
        </Button>
    );

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
            <Typography.Title level={5} style={{ marginBottom: 0 }}>
                2. Sync
            </Typography.Title>
            <div style={{ margin: '0.5rem 0' }}>
                <small style={{ color: token.colorTextTertiary }}>
                    <TbPlugConnected style={{ verticalAlign: '-0.1rem', marginRight: '0.25rem' }} />
                    Data Source Syncing
                </small>
            </div>
            <Dropdown
                trigger={['click']}
                popupRender={() => (
                    <DropdownMenuPanel>
                        <Row style={{ margin: '0 0.75rem' }}>
                            <Col flex='auto'>
                                <small style={{ color: token.colorTextTertiary }}>Existing Data Sources</small>
                            </Col>
                        </Row>
                        <Menu.Divider />
                        {loadingConnectedAccounts && syncableConnectedAccounts.length === 0 ? (
                            <Menu.Item disabled key='loading-connected-accounts'>
                                <BaseSkeleton title={false} paragraph={{ rows: 3 }} />
                            </Menu.Item>
                        ) : syncableConnectedAccounts?.length !== 0 ? (
                            syncableConnectedAccounts?.map((ca: any) => {
                                return (
                                    <Menu.Item
                                        disabled={selectedConnectedAccounts?.some((a: any) => a?.id === ca?.id)}
                                        key={ca?.id}
                                        onClick={() => {
                                            setSelectedConnectedAccounts([...selectedConnectedAccounts, ca]);
                                        }}
                                    >
                                        <Row>
                                            <Col flex='auto'>
                                                <Avatar
                                                    shape='square'
                                                    style={{
                                                        borderRadius: token.borderRadius,
                                                        backgroundColor: token.colorBgBase,
                                                        border: `1px solid ${token.colorBorder}`,
                                                        marginRight: '0.375rem',
                                                        verticalAlign: 0,
                                                        padding: '0.125rem',
                                                    }}
                                                    size={16}
                                                    icon={<img src={typeToIconMap[ca?.type]} />}
                                                />
                                                {ca?.type}
                                                {selectedConnectedAccounts?.some((a: any) => a?.id === ca?.id) && (
                                                    <SmallTag message={<>Already Selected</>} />
                                                )}
                                            </Col>
                                        </Row>
                                        <small style={{ color: token.colorTextTertiary }}>
                                            Connected {ca?.createdAt && formatDate(ca?.createdAt)}
                                        </small>
                                    </Menu.Item>
                                );
                            })
                        ) : (
                            <div style={{ margin: '1.5rem' }}>
                                <EmptySection
                                    height='10rem'
                                    icon={<TbPlugConnected size='1rem' style={{ color: token.colorTextTertiary }} />}
                                    label='No Data Sources'
                                    description='Connect Google Sheets or Notion before syncing company data from Search.'
                                    extra={renderManageDataSourcesButton()}
                                />
                            </div>
                        )}
                    </DropdownMenuPanel>
                )}
            >
                <DropdownButton block style={{ justifyItems: 'flex-start' }}>
                    <Row style={{ width: '100%' }}>
                        <Col flex='auto' style={{ textAlign: 'left' }}>
                            <TbPlugConnected
                                style={{
                                    verticalAlign: '-0.15rem',
                                    marginRight: '0.25rem',
                                }}
                            />
                            Sync with Data Source...
                        </Col>
                    </Row>
                </DropdownButton>
            </Dropdown>
            <DashedPane height='auto' style={{ flex: 1, minHeight: 0 }}>
                {selectedConnectedAccounts?.length !== 0 ? (
                    <List
                        size='small'
                        style={{ height: '100%', overflowY: 'auto' }}
                        dataSource={selectedConnectedAccounts}
                        renderItem={(sca: any) => {
                            return (
                                <div
                                    key={sca?.id}
                                    style={{
                                        borderBottom:
                                            selectedConnectedAccounts?.length > 1
                                                ? `1px solid ${token.colorBorder}`
                                                : 'none',
                                        paddingBottom: '0.5rem',
                                    }}
                                >
                                    <List.Item style={{ padding: '0.5rem 0', borderBottom: 'none' }}>
                                        <List.Item.Meta
                                            avatar={
                                                <Avatar
                                                    style={{
                                                        marginTop: '0.25rem',
                                                        backgroundColor: token.colorBgBase,
                                                        border: `1px solid ${token.colorBorder}`,
                                                    }}
                                                    size='large'
                                                    shape='square'
                                                    icon={
                                                        <img
                                                            alt={sca?.type}
                                                            src={typeToIconMap[sca?.type]}
                                                            style={{
                                                                ...imgLogoStyling,
                                                                transform: 'scale(0.75)',
                                                            }}
                                                        />
                                                    }
                                                />
                                            }
                                            title={<span style={{ fontWeight: 500 }}>{sca?.type}</span>}
                                            description={
                                                <small>Connected {sca?.createdAt && formatDate(sca?.createdAt)}</small>
                                            }
                                        />
                                        <Button
                                            type='dashed'
                                            onClick={() => {
                                                const data = selectedConnectedAccounts.filter(
                                                    (fsca: any) => sca?.id !== fsca?.id,
                                                );

                                                setSelectedConnectedAccounts(data);
                                            }}
                                        >
                                            <TbX
                                                size='1rem'
                                                style={{ verticalAlign: '-0.175rem', color: token.colorTextTertiary }}
                                            />
                                        </Button>
                                    </List.Item>
                                </div>
                            );
                        }}
                    />
                ) : (
                    <EmptySection
                        height='100%'
                        icon={<TbDatabaseCog size='1rem' style={{ color: token.colorTextTertiary }} />}
                        label='No Data Sources Selected'
                        description='Choose one or more existing data sources from the dropdown to continue.'
                        extra={renderManageDataSourcesButton()}
                    />
                )}
            </DashedPane>
            <AddDataSourceModal open={addDataSourceModalOpen} setOpen={setAddDataSourceModalOpen} activeView='manage' />
        </div>
    );
};

export default DataSources;
